import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { textLinkVariants } from '@/components/ui/button-variants';
import { useOrg } from '@/contexts/OrgContext';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { usePartyNames } from '@/hooks/usePartyNames';
import { getFirestoreDb } from '@/lib/firebase';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

type Bill = {
  id: string;
  date: string;
  status: string;
  grossTotal?: string;
  vendorId?: string;
};

type Vendor = { id: string; code: string; name: string; balance: string };

export function PurchasesPage() {
  const { t } = useTranslation();
  const { orgId, currentPeriodId } = useOrg();
  const online = useOnlineStatus();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'bills' | 'vendors'>('bills');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  const mapBill = useCallback(
    (id: string, data: Record<string, unknown>) => ({ id, ...data }) as Bill,
    []
  );
  const mapVendor = useCallback(
    (id: string, data: Record<string, unknown>) => ({
      id,
      code: String(data.code ?? ''),
      name: String(data.name ?? ''),
      balance: String(data.balance ?? '0.00')
    }),
    []
  );

  const bills = useFirestorePageQuery<Bill>({
    orgId,
    collectionPath: 'purchaseBills',
    orderByField: 'updatedAt',
    orderDirection: 'desc',
    mapDoc: mapBill,
    enabled: tab === 'bills'
  });

  const vendors = useFirestorePageQuery<Vendor>({
    orgId,
    collectionPath: 'vendors',
    orderByField: 'name',
    orderDirection: 'asc',
    mapDoc: mapVendor,
    enabled: tab === 'vendors'
  });

  const vendorIds = useMemo(
    () => bills.rows.map((b) => b.vendorId ?? '').filter(Boolean),
    [bills.rows]
  );
  const vendorMap = usePartyNames(orgId, 'vendors', vendorIds);

  async function addVendor() {
    if (!orgId || !online || !code.trim() || !name.trim()) return;
    await addDoc(collection(getFirestoreDb(), 'organizations', orgId, 'vendors'), {
      code: code.trim(),
      name: name.trim(),
      balance: '0.00',
      active: true,
      createdAt: serverTimestamp()
    });
    setCode('');
    setName('');
    vendors.refresh();
  }

  async function newBill() {
    if (!orgId || !currentPeriodId || !online) return;
    const first = vendors.rows[0];
    if (!first) return;
    const today = new Date().toISOString().slice(0, 10);
    const ref = await addDoc(collection(getFirestoreDb(), 'organizations', orgId, 'purchaseBills'), {
      vendorId: first.id,
      date: today,
      dueDate: today,
      periodId: currentPeriodId,
      status: 'draft',
      lines: [{ quantity: 1, unitPrice: '0.00', taxRatePercent: 0 }],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    navigate(`/purchases/bills/${ref.id}`);
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('nav.purchasesAp')}</h1>
      <SegmentedControl
        className="mt-4 max-w-md"
        items={[
          { id: 'bills', label: t('purchases.bills') },
          { id: 'vendors', label: t('purchases.vendors') }
        ]}
        value={tab}
        onChange={(id) => setTab(id as 'bills' | 'vendors')}
      />
      {tab === 'vendors' && (
        <div className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            <input
              className="rounded border px-3 py-2 text-sm"
              placeholder={t('coa.code')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <input
              className="rounded border px-3 py-2 text-sm"
              placeholder={t('coa.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button onClick={() => void addVendor()} disabled={!online}>
              {t('purchases.addVendor')}
            </Button>
          </div>
          <PaginatedTable
            loading={vendors.loading}
            pageIndex={vendors.pageIndex}
            hasNext={vendors.hasNext}
            hasPrev={vendors.hasPrev}
            onNext={vendors.nextPage}
            onPrev={vendors.prevPage}
            pageSize={vendors.pageSize}
            onPageSizeChange={vendors.setPageSize}
          >
            <table className="w-full text-sm">
              <tbody>
                {vendors.rows.map((v) => (
                  <tr key={v.id} className="border-t border-[var(--fv-border)]">
                    <td className="p-2">{v.code}</td>
                    <td className="p-2">{v.name}</td>
                    <td className="p-2 text-end"><MoneyDisplay value={v.balance} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PaginatedTable>
        </div>
      )}
      {tab === 'bills' && (
        <div className="mt-6">
          <Button onClick={() => void newBill()} disabled={!online || !vendors.rows.length}>
            {t('purchases.newBill')}
          </Button>
          <PaginatedTable
            className="mt-4"
            loading={bills.loading}
            pageIndex={bills.pageIndex}
            hasNext={bills.hasNext}
            hasPrev={bills.hasPrev}
            onNext={bills.nextPage}
            onPrev={bills.prevPage}
            pageSize={bills.pageSize}
            onPageSizeChange={bills.setPageSize}
          >
            <table className="w-full text-sm">
              <tbody>
                {bills.rows.map((b) => (
                  <tr key={b.id} className="border-t border-[var(--fv-border)]">
                    <td className="p-2">{b.date}</td>
                    <td className="p-2">{vendorMap.get(b.vendorId ?? '') || b.vendorId || '—'}</td>
                    <td className="p-2">{b.status}</td>
                    <td className="p-2 text-end"><MoneyDisplay value={b.grossTotal} /></td>
                    <td className="p-2">
                      <Link to={`/purchases/bills/${b.id}`} className={textLinkVariants()}>
                        {t('sales.open')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PaginatedTable>
        </div>
      )}
    </div>
  );
}
